import React, { useEffect } from 'react';
import WidgetPageShell from '@components/common/WidgetPageShell';
import WidgetNav from '@components/common/WidgetNav';
import FooterBrand from '@components/common/FooterBrand';
import mockPerformanceTrends from '@data/mockPerformanceTrends';
import {
  mapBalanceSheetApiToFinancialSection,
  mapCashFlowApiToFinancialSection,
  mapResearchDevelopmentApiToFinancialSection,
} from '@apis/performanceTrendsMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useBalanceSheet } from '@hooks/useBalanceSheet';
import { useCashFlow } from '@hooks/useCashFlow';
import { useResearchDevelopment } from '@hooks/useResearchDevelopment';
import { notify } from '@utils/notify';
import TrendsQuickLinks from './components/TrendsQuickLinks';
import AnnualPerformanceSection from './components/AnnualPerformanceSection';
import QuarterlyPerformanceSection from './components/QuarterlyPerformanceSection';
import BalanceSheetSection from './components/BalanceSheetSection';
import CashFlowSection from './components/CashFlowSection';
import ResearchDevelopmentSection from './components/ResearchDevelopmentSection';
import './PerformanceTrendsPage.less';

/**
 * Performance Trends screen (PT09).
 * Web parent 3490:22981 (626px), mobile parent 3158:5949 (375px).
 * Section order: nav, quick links, annual, quarterly, balance sheet,
 * cash flow, research/development, footer.
 *
 * Annual, Quarterly, Balance Sheet, Cash Flow, and Research/Development are
 * API-backed through `/api/fundamentals/*`.
 */
const PerformanceTrendsPage: React.FC = () => {
  const data = mockPerformanceTrends;
  const balanceSheetQuery = useBalanceSheet();
  const cashFlowQuery = useCashFlow();
  const researchDevelopmentQuery = useResearchDevelopment();

  useEffect(() => {
    if (balanceSheetQuery.isError && shouldNotifyDataRequestError(balanceSheetQuery.error)) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(balanceSheetQuery.error) ??
          '貸借対照表の取得に失敗しました。',
      });
    }
  }, [balanceSheetQuery.error, balanceSheetQuery.isError]);

  useEffect(() => {
    if (cashFlowQuery.isError && shouldNotifyDataRequestError(cashFlowQuery.error)) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(cashFlowQuery.error) ??
          'キャッシュフローの取得に失敗しました。',
      });
    }
  }, [cashFlowQuery.error, cashFlowQuery.isError]);

  useEffect(() => {
    if (
      researchDevelopmentQuery.isError &&
      shouldNotifyDataRequestError(researchDevelopmentQuery.error)
    ) {
      notify({
        level: 'error',
        message: getApiParamErrorNotificationMessage(researchDevelopmentQuery.error) ??
          '研究開発設備投資の取得に失敗しました。',
      });
    }
  }, [researchDevelopmentQuery.error, researchDevelopmentQuery.isError]);

  // Undefined query data maps to a no-data section so loading/errors never show
  // mock financial values as if they were live.
  const balanceSheet = mapBalanceSheetApiToFinancialSection(
    balanceSheetQuery.data,
    data.balanceSheet,
  );
  const cashFlow = mapCashFlowApiToFinancialSection(
    cashFlowQuery.data,
    data.cashFlow,
  );
  const researchDevelopment = mapResearchDevelopmentApiToFinancialSection(
    researchDevelopmentQuery.data,
    data.researchDevelopment,
  );

  return (
    <WidgetPageShell className="performance-trends">
      <WidgetNav />
      <main className="performance-trends__body">
        <TrendsQuickLinks links={data.quickLinks} nodeId="3158:5213" />
        <AnnualPerformanceSection data={data.annual} />
        <QuarterlyPerformanceSection data={data.quarterly} />
        <BalanceSheetSection data={balanceSheet} />
        <CashFlowSection data={cashFlow} />
        <ResearchDevelopmentSection data={researchDevelopment} />
        <FooterBrand className="performance-trends__footer" />
      </main>
    </WidgetPageShell>
  );
};

export default PerformanceTrendsPage;
